/**
 * Format a number as abbreviated currency (e.g., $14M, ($2.1M))
 * Negative numbers are displayed with parentheses: ($408.7K)
 */
export function formatCurrency(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') return '-'

  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'

  const absValue = Math.abs(num)
  const isNegative = num < 0

  // Lower bounds account for one-decimal rounding into the next tier:
  // e.g. 999_950 → 999.95K rounds to 1000.0K, so display it as $1M.
  let formatted: string
  if (absValue >= 999_950_000) {
    formatted = `$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`
  } else if (absValue >= 999_950) {
    formatted = `$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  } else if (absValue >= 1_000) {
    formatted = `$${(absValue / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  } else {
    formatted = `$${absValue.toFixed(0)}`
  }

  return isNegative ? `(${formatted})` : formatted
}

/**
 * Format a number as percentage (e.g., 28%)
 */
export function formatPercentage(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') return '-'

  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'

  return `${num.toFixed(1).replace(/\.0$/, '')}%`
}

type NumberFormatter = (value: number | string | null | undefined) => string

const formatters: Record<string, NumberFormatter> = {
  currency: formatCurrency,
  percentage: formatPercentage,
}

/**
 * Get formatter function by format name.
 * Handles both lowercase (legacy) and uppercase (API) format values.
 */
export function getFormatter(
  format: string | undefined
): NumberFormatter | undefined {
  if (!format) return undefined
  // Normalize to lowercase to handle API values like 'CURRENCY' and 'PERCENTAGE'
  return formatters[format.toLowerCase()]
}

/**
 * Format a date as "Jan 15, 2026" format
 * Uses UTC to avoid timezone shifts for date-only fields
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d)
}
